import { useState, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { SessionCard, type SessionCardProps } from '@/components/profile/active-sessions-card';
import { authApi } from '@/lib/pillarApi';
import type {
    ListSessionsPageResponse,
    SessionListItem,
} from '@anchorbase/pillar-sdk';

function SessionTerminateDialog(props: {
    readonly session: SessionListItem | null;
    readonly open: boolean;
    readonly isSubmitting: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly onConfirm: () => void;
}): ReactElement {
    return (
        <Dialog open={props.open} onOpenChange={props.onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>End Session</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to terminate this session?
                    </DialogDescription>
                </DialogHeader>
                {props.session !== null ? (
                    <div className="py-4">
                        <div className="space-y-2 text-sm">
                            <div>
                                <span className="font-medium">User:</span>{' '}
                                {props.session.user_email}
                            </div>
                            <div>
                                <span className="font-medium">Session:</span>{' '}
                                <code className="text-xs">{props.session.session_uuid}</code>
                            </div>
                        </div>
                    </div>
                ) : null}
                <DialogFooter>
                    <Button variant="outline" onClick={() => props.onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={props.onConfirm}
                        disabled={props.isSubmitting}
                    >
                        {props.isSubmitting ? 'Ending...' : 'End Session'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function AdminSessionsListPage(): ReactElement {
    const queryClient = useQueryClient();
    const [sessionToTerminate, setSessionToTerminate] = useState<SessionListItem | null>(null);
    const [showTerminateDialog, setShowTerminateDialog] = useState(false);

    const terminateSessionMutation = useMutation({
        mutationFn: async (session: SessionListItem) => {
            if (session.session_uuid === undefined) {
                throw new Error('Missing session_uuid for admin session termination');
            }
            if (session.user_uuid === undefined) {
                throw new Error('Missing user_uuid for admin session termination');
            }
            return await authApi.expireSessionAdmin({
                session_uuid: session.session_uuid,
                user_uuid: session.user_uuid,
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['adminSessionsPage'],
            });
        },
    });

    const toggleConsoleMutation = useMutation({
        mutationFn: async (payload: {
            readonly session: SessionListItem;
            readonly enabled: boolean;
        }) => {
            if (payload.session.session_uuid === undefined) {
                throw new Error('Missing session_uuid for session console updates');
            }
            return await authApi.setSessionConsoleAdmin({
                session_uuid: payload.session.session_uuid,
                enable_javascript_console: payload.enabled,
            });
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['adminSessionsPage'],
            });
        },
    });

    const renderSessionCard = (session: SessionListItem): ReactElement => {
        const sessionCardProps: SessionCardProps = {
            session,
            mode: 'admin',
            isCurrentSession: session.is_current,
            onTerminate: () => {
                setSessionToTerminate(session);
                setShowTerminateDialog(true);
            },
            isTerminating: terminateSessionMutation.isPending,
            onToggleConsoleLogging: (currentSession, enabled) => {
                toggleConsoleMutation.mutate({ session: currentSession, enabled });
            },
            isTogglingConsole: toggleConsoleMutation.isPending,
        };

        return <SessionCard key={session.session_uuid ?? session.user_email} {...sessionCardProps} />;
    };

    return (
        <>
            <InfiniteScrollList<SessionListItem, string, readonly ['adminSessionsPage']>
                pageTitle="Session Management"
                cardTitle="Active Sessions"
                queryPlaceholder="Search by user email, session UUID, IP address, or user agent"
                queryKeyPrefix={['adminSessionsPage']}
                pageSize={100}
                fetchPage={async (params) => {
                    const response: ListSessionsPageResponse = await authApi.listAllSessionsPage({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                    return { items: response.sessions, pagination: response.pagination };
                }}
                parseCursorParam={(pageParam: unknown) => {
                    if (pageParam === null || pageParam === undefined) {
                        return undefined;
                    }
                    const parseResult = z.string().safeParse(pageParam);
                    if (!parseResult.success) {
                        throw new Error('Expected string cursor');
                    }
                    return parseResult.data;
                }}
                stateCopy={{
                    loadingLabel: 'Loading sessions...',
                    emptyLabel: 'No sessions found.',
                    endOfListLabel: 'End of sessions.',
                    genericErrorLabel: 'Failed to load sessions.',
                }}
                renderItems={({ items }) => {
                    return <div className="space-y-3">{items.map(renderSessionCard)}</div>;
                }}
            />
            <SessionTerminateDialog
                session={sessionToTerminate}
                open={showTerminateDialog}
                isSubmitting={terminateSessionMutation.isPending}
                onOpenChange={(open) => {
                    setShowTerminateDialog(open);
                    if (!open) {
                        setSessionToTerminate(null);
                    }
                }}
                onConfirm={() => {
                    if (sessionToTerminate === null) {
                        return;
                    }
                    terminateSessionMutation.mutate(sessionToTerminate, {
                        onSuccess: () => {
                            setShowTerminateDialog(false);
                            setSessionToTerminate(null);
                        },
                    });
                }}
            />
        </>
    );
}
